import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { VersionWatcher } from '@/components/VersionWatcher';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from './hooks/useAuth';
import { useTenantStore } from './hooks/useTenantStore';
import Index from './pages/Index';
import Auth from './pages/Auth';
import AuthVerify from './pages/AuthVerify';
import Dashboard from './pages/Dashboard';
import Onboarding from './pages/Onboarding';
import Companies from './pages/Companies';
import Transactions from './pages/Transactions';
import CompanyDetail from './pages/CompanyDetail';
import Compliance from './pages/Compliance';
import Ella360 from './pages/compliance/Ella360';
import RiskHealth from './pages/compliance/RiskHealth';
import RiskAdmin from './pages/compliance/RiskAdmin';
import EllaAdvisor from './pages/compliance/EllaAdvisor';
import EllaKnowledge from './pages/compliance/EllaKnowledge';
import EllaLearn from './pages/compliance/EllaLearn';
import Settings from './pages/Settings';
import CompanySettings from './pages/CompanySettings';
import DataRoom from './pages/DataRoom';
import DataRoomAccess from './pages/DataRoomAccess';
import InvestorPortal from './pages/InvestorPortal';
import InvestorPortfolioAnalytics from './pages/InvestorPortfolioAnalytics';
import PortfolioSensitivity from './pages/PortfolioSensitivity';
import BoardPortal from './pages/BoardPortal';
import LiquidityDashboardPage from './pages/LiquidityDashboard';
import SmartMatchingPanel from './pages/SmartMatchingPanel';
import OnChainReceiptsPage from './pages/OnChainReceiptsPage';
import ComplianceProofs from './pages/compliance/ComplianceProofs';
import PublicProofExplorer from './pages/compliance/PublicProofExplorer';
import PublicAuditPortal from './pages/compliance/PublicAuditPortal';
import AuditConsole from './pages/compliance/AuditConsole';
import MultiCompanyAudit from './pages/compliance/MultiCompanyAudit';
import RegulatorPortal from '@/pages/compliance/RegulatorPortal';
import RegulatorRolePortal from '@/pages/compliance/RegulatorRolePortal';
import FundingScenarios from '@/pages/FundingScenarios';
import FundingRoundSetup from '@/pages/FundingRoundSetup';
import ValuationAdvisor from '@/pages/ValuationAdvisor';
import ShareClassConversion from '@/pages/ShareClassConversion';
import SuperAdmin from '@/pages/SuperAdmin';
import NDAManagement from '@/pages/NDAManagement';
import AuditTrail from '@/pages/AuditTrail';
import NotFound from '@/pages/NotFound';
import Privacy from '@/pages/Privacy';
import Cookies from '@/pages/Cookies';
import Terms from '@/pages/Terms';
import DataProtection from '@/pages/DataProtection';
import CompliancePage from '@/pages/CompliancePage';
import Security from '@/pages/Security';
import AboutSpesvest from '@/pages/AboutSpesvest';
import Pricing from '@/pages/Pricing';
import NDAAcceptance from '@/pages/NDAAcceptance';
import VisitorAnalytics from '@/pages/analytics/VisitorAnalytics';
import ABTesting from '@/pages/analytics/ABTesting';
import Shareholders from '@/pages/Shareholders';
import { NDAAgreementGuard } from '@/components/legal/NDAAgreementGuard';
import { useVisitorTracking } from '@/hooks/useVisitorTracking';

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

function SentryUserContext() {
  const { user } = useAuth();
  const { tenantId } = useTenantStore();

  // Initialize visitor tracking
  useVisitorTracking();

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email || undefined,
        tenant: tenantId || undefined,
      });
      Sentry.setTag('build', import.meta.env.VITE_APP_VERSION);
      Sentry.setTag('environment', import.meta.env.MODE);
    } else {
      Sentry.setUser(null);
    }
  }, [user, tenantId]);

  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <TooltipProvider>
        <SentryUserContext />
        <Toaster />
        <Sonner />
        <Sentry.ErrorBoundary
          fallback={
            <div className="min-h-screen flex items-center justify-center">
              An error has occurred
            </div>
          }
        >
          <BrowserRouter>
            {/* Inside the router: it re-checks on navigation. */}
            <VersionWatcher />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/verify" element={<AuthVerify />} />
              <Route path="/dataroom-access" element={<DataRoomAccess />} />
              <Route path="/demo" element={<Index />} />
              <Route
                path="/nda-acceptance"
                element={
                  <ProtectedRoute>
                    <NDAAcceptance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <NDAAgreementGuard>
                      <Dashboard />
                    </NDAAgreementGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies"
                element={
                  <ProtectedRoute>
                    <Companies />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/:companyId"
                element={
                  <ProtectedRoute>
                    <CompanyDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transactions"
                element={
                  <ProtectedRoute>
                    <Transactions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/funding/scenarios"
                element={
                  <ProtectedRoute>
                    <FundingScenarios />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/funding/valuation"
                element={
                  <ProtectedRoute>
                    <ValuationAdvisor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/funding/round-setup"
                element={
                  <ProtectedRoute>
                    <FundingRoundSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/share-class-conversion"
                element={
                  <ProtectedRoute>
                    <ShareClassConversion />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance"
                element={
                  <ProtectedRoute>
                    <Compliance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance/ella360"
                element={
                  <ProtectedRoute>
                    <Ella360 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance/risk-health"
                element={
                  <ProtectedRoute>
                    <RiskHealth />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance/proofs"
                element={
                  <ProtectedRoute>
                    <ComplianceProofs />
                  </ProtectedRoute>
                }
              />
              <Route path="/public/verify-proof" element={<PublicProofExplorer />} />
              <Route path="/audit/:companyId" element={<PublicAuditPortal />} />
              <Route path="/regulator-portal" element={<RegulatorPortal />} />
              <Route path="/regulator/portal" element={<RegulatorRolePortal />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/data-protection" element={<DataProtection />} />
              <Route path="/compliance-statement" element={<CompliancePage />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/security" element={<Security />} />
              <Route path="/about" element={<AboutSpesvest />} />
              <Route
                path="/compliance/risk-admin"
                element={
                  <ProtectedRoute>
                    <RiskAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance/ella-advisor"
                element={
                  <ProtectedRoute>
                    <EllaAdvisor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance/ella-knowledge"
                element={
                  <ProtectedRoute>
                    <EllaKnowledge />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance/ella-learn"
                element={
                  <ProtectedRoute>
                    <EllaLearn />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance/audit-console"
                element={
                  <ProtectedRoute>
                    <AuditConsole />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance/multi-company-audit"
                element={
                  <ProtectedRoute>
                    <MultiCompanyAudit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data-room"
                element={
                  <ProtectedRoute>
                    <DataRoom />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/company"
                element={
                  <ProtectedRoute>
                    <CompanySettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/board"
                element={
                  <ProtectedRoute>
                    <BoardPortal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investor-portal"
                element={
                  <ProtectedRoute>
                    <NDAAgreementGuard>
                      <InvestorPortal />
                    </NDAAgreementGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investor-portal/portfolio"
                element={
                  <ProtectedRoute>
                    <InvestorPortfolioAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investor-portal/sensitivity"
                element={
                  <ProtectedRoute>
                    <PortfolioSensitivity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/liquidity"
                element={
                  <ProtectedRoute>
                    <LiquidityDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/marketplace/smart-matching"
                element={
                  <ProtectedRoute>
                    <SmartMatchingPanel />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/on-chain-receipts"
                element={
                  <ProtectedRoute>
                    <OnChainReceiptsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compliance/proofs"
                element={
                  <ProtectedRoute>
                    <ComplianceProofs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/super-admin"
                element={
                  <ProtectedRoute>
                    <SuperAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nda-management"
                element={
                  <ProtectedRoute>
                    <NDAManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit-trail"
                element={
                  <ProtectedRoute>
                    <AuditTrail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/visitors"
                element={
                  <ProtectedRoute>
                    <VisitorAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/ab-testing"
                element={
                  <ProtectedRoute>
                    <ABTesting />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shareholders"
                element={
                  <ProtectedRoute>
                    <Shareholders />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </Sentry.ErrorBoundary>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
